import React, { useCallback } from "react";
import { Modal, ModalTitle, useConfirmModal, useToasts } from "@td/ui";
import { pick } from "lodash";
import ProviderPanelForm from "./ProviderPanelForm";
import { translate } from "@td/shared_utils";
import { useModals } from "../contexts/modals-context";
import { destroyProviderPanel } from "../apis";
import { updateProviderPanel } from "../apis/updateProviderPanel";
import { createProviderPanel } from "../apis/createProviderPanel";
import { useProviderPanel } from "../contexts/provider-panel-context";
import { useProviderPanels } from "../contexts/provider-panels-context";
import { usePracticeTeams } from "../contexts/practice-teams-context";
import { ModalSubtitle } from "../components";
import { buildCreatePanelRequest, buildUpdatePanelRequest, initialValuesFactory } from "../utils";
import { NATIONAL } from "../constants";
const PANEL_DESTROY_SUCCESS_MESSAGE = translate(null, "provider_panel.notifications", "provider_panel_destroyed");
const PANEL_DESTROY_CONFIRMATION_TITLE = translate(null, "provider_panel.notifications.provider_panel_delete_confirmation", "title");
const PANEL_DESTROY_CONFIRMATION_DESCRIPTION = translate(null, "provider_panel.notifications.provider_panel_delete_confirmation", "description");

const ProviderPanelModal = React.memo(() => {
  const toast = useToasts();
  const { closeModal } = useModals();
  const { provider, practiceTeamParticipant, isPrincipal } = useProviderPanel();
  const { fetchProviderPanels, specialtyCode } = useProviderPanels();
  const { confirm } = useConfirmModal();

  const practiceTeams = usePracticeTeams();
  const defaultPrincipalPractice = pick(
    practiceTeams.find(({ label }) => label === NATIONAL),
    ['value', 'label']
  );

  const resolveRequest = useCallback(async (values) => {
    if (practiceTeamParticipant) {
      return await updateProviderPanel(
        buildUpdatePanelRequest(practiceTeamParticipant.id, values, { specialtyCode, isPrincipal })
      );
    } else {
      return await createProviderPanel(
        buildCreatePanelRequest(provider.id, values, {
          specialtyCode,
          isPrincipal,
          defaultPracticeId: defaultPrincipalPractice.value
        })
      );
    }
  }, [provider, practiceTeamParticipant, isPrincipal, specialtyCode]);

  const handleSubmit = useCallback(async (values) => {
    const { errors } = await resolveRequest(values);

    if (!errors.length) {
      fetchProviderPanels();

      toast.success({
        description: translate(
          null,
          "provider_panel.notifications",
          practiceTeamParticipant ? "provider_panel_updated" : "provider_panel_created",
        ),
      });

      closeModal();
    }
  }, [practiceTeamParticipant, fetchProviderPanels, closeModal]);

  const handleDelete = useCallback(async () => {
    if (!practiceTeamParticipant) {
      return;
    }

    const isConfirmed = await confirm({
      title: PANEL_DESTROY_CONFIRMATION_TITLE,
      description: PANEL_DESTROY_CONFIRMATION_DESCRIPTION,
    });

    if (!isConfirmed) {
      return;
    }

    const { success } = await destroyProviderPanel(practiceTeamParticipant.id);

    if (success) {
      toast.success({
        description: PANEL_DESTROY_SUCCESS_MESSAGE,
      });

      fetchProviderPanels();
      closeModal();
    }
  }, [practiceTeamParticipant, fetchProviderPanels, closeModal]);

  const modalTitle = translate(null, "provider_panel.modal", "title", { name: provider.nameWithCredentials });

  const initialValues = initialValuesFactory({
    provider,
    practiceTeamParticipant,
    isPrincipal,
    specialtyCode,
  });

  return (
    <Modal
      isOpen={true}
      onClose={closeModal}
      title={modalTitle}
    >
      <ModalTitle
        title={modalTitle}
      />
      <ModalSubtitle>
        {provider.paymentMethod}
      </ModalSubtitle>
      <ProviderPanelForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isDeletable={practiceTeamParticipant && !isPrincipal}
        onDelete={handleDelete}
      />
    </Modal>
  );
});

ProviderPanelModal.displayName = "ProviderPanelModal";

export default ProviderPanelModal;
